import axios from 'axios'

// Function to analyze a website and extract business information
export const analyzeWebsite = async (websiteUrl: string): Promise<any> => {
  try {
    // Step 1: Fetch the website content
    const websiteContent = await fetchWebsiteContent(websiteUrl)
    if (!websiteContent) {
      throw new Error('Could not fetch website content')
    }
    // Step 2: Analyze the content using OpenAI
    const analysis = await analyzeWebsiteContentWithAI(
      websiteContent,
      websiteUrl,
    )
    return analysis
  } catch (error) {
    console.error('Website analysis error:', error)
    throw new Error(`Failed to analyze website: ${error.message}`)
  }
}

// Function to fetch website content
const fetchWebsiteContent = async (url: string): Promise<string> => {
  try {
    // Use a CORS proxy to fetch the website content
    // Note: In a production environment, you might want to use a server-side solution
    const corsProxyUrl = `https://corsproxy.io/?${encodeURIComponent(url)}`
    const response = await axios.get(corsProxyUrl, {
      timeout: 10000, // 10 seconds timeout
      headers: {
        Accept:
          'text/html,application/xhtml+xml,application/xml;q=0.9,image/webp,*/*;q=0.8',
        'User-Agent':
          'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36 (KHTML, like Gecko) Chrome/91.0.4472.124 Safari/537.36',
      },
    })
    return response.data
  } catch (error) {
    console.error('Error fetching website content:', error)
    // Try an alternative approach - using allorigins proxy
    try {
      const allOriginsUrl = `https://api.allorigins.win/raw?url=${encodeURIComponent(url)}`
      const response = await axios.get(allOriginsUrl, { timeout: 10000 })
      return response.data
    } catch (secondError) {
      console.error('Error using alternative proxy:', secondError)
      throw new Error('Failed to fetch website content after multiple attempts')
    }
  }
}

// Function to analyze website content using OpenAI
const analyzeWebsiteContentWithAI = async (
  content: string,
  websiteUrl: string,
): Promise<any> => {
  try {
    // Truncate content if it's too large (OpenAI has token limits)
    const truncatedContent = content.substring(0, 15000) // Limiting to ~15K characters

    // Prepare the prompt for OpenAI
    const prompt = `
      You are a business analyst. Analyze the following website content and extract detailed business information.
      Website URL: ${websiteUrl}
      Extract the following information:
      1. Industry: The specific industry or sector the business operates in
      2. Business Size: Estimated size (small, medium, large) and if available, employee count
      3. Customer Type: B2B, B2C, or both
      4. Positioning: The unique value proposition or market positioning
      5. Products/Services: Key products or services offered
      6. Target Audience: Who are their ideal customers
      7. Geographic Focus: Where they primarily operate (local, national, global)
      8. Company Age: How long they've been in business (if available)
      9. Key Competitors: Major competitors in their space
      10. Business Model: How they generate revenue
      11. Marketing Approach: Key marketing strategies they appear to use
      12. Technology Stack: Any visible technologies they're using
      13. Business Challenges: Common challenges businesses in this industry face
      14. Growth Opportunities: Potential areas for business growth
      15. Summary: A concise overview of the business
      Website Content:
      ${truncatedContent}
      Provide a detailed analysis with all the information above in JSON format. If any information is not available, indicate "Not available" for that field.
    `

    // Call OpenAI API through the proxy
    const response = await axios.post(
      'https://requests.magicpatterns.com/openai/proxy',
      {
        model: 'gpt-4-turbo',
        messages: [
          {
            role: 'system',
            content:
              'You are a business analyst that extracts detailed business information from website content.',
          },
          {
            role: 'user',
            content: prompt,
          },
        ],
        temperature: 0.5,
        max_tokens: 2000,
      },
    )

    // Parse the response to extract the business information
    const aiResponse = response.data.choices[0].message.content

    // Try to parse JSON from the response
    try {
      // First check if the response contains a JSON block
      const jsonMatch =
        aiResponse.match(/```json\n([\s\S]*?)\n```/) ||
        aiResponse.match(/```\n([\s\S]*?)\n```/) ||
        aiResponse.match(/{[\s\S]*?}/)
      const jsonContent = jsonMatch ? jsonMatch[1] || jsonMatch[0] : aiResponse
      // Clean the string and parse it
      const cleanedJson = jsonContent.replace(/```json|```/g, '').trim()
      const parsedData = JSON.parse(cleanedJson)
      return parsedData
    } catch (parseError) {
      console.error('Failed to parse AI response as JSON:', parseError)
      // Fallback to a more structured extraction if JSON parsing fails
      const analysis = {
        industry: extractField(aiResponse, 'Industry'),
        businessSize: extractField(aiResponse, 'Business Size'),
        customerType: extractField(aiResponse, 'Customer Type'),
        positioning: extractField(aiResponse, 'Positioning'),
        productsServices: extractField(aiResponse, 'Products/Services'),
        targetAudience: extractField(aiResponse, 'Target Audience'),
        geographicFocus: extractField(aiResponse, 'Geographic Focus'),
        companyAge: extractField(aiResponse, 'Company Age'),
        keyCompetitors: extractField(aiResponse, 'Key Competitors'),
        businessModel: extractField(aiResponse, 'Business Model'),
        marketingApproach: extractField(aiResponse, 'Marketing Approach'),
        technologyStack: extractField(aiResponse, 'Technology Stack'),
        businessChallenges: extractField(aiResponse, 'Business Challenges'),
        growthOpportunities: extractField(aiResponse, 'Growth Opportunities'),
        summary: extractField(aiResponse, 'Summary'),
      }
      return analysis
    }
  } catch (error) {
    console.error('Error analyzing website with AI:', error)
    // Return a basic analysis with error information
    return {
      industry: 'Could not determine',
      businessSize: 'Could not determine',
      customerType: 'Could not determine',
      positioning: 'Could not determine',
      summary:
        'Could not analyze website content. Please try again or enter information manually.',
      error: error.message,
    }
  }
}

// Helper function to extract fields from text response
const extractField = (text: string, fieldName: string): string => {
  const regex = new RegExp(
    `${fieldName}[:\\s]+(.*?)(?=\\n\\d+\\.|\\n[A-Z]|$)`,
    's',
  )
  const match = text.match(regex)
  return match ? match[1].trim() : 'Not available'
}

export const generateQuestionnaire = async (clientData: any): Promise<any> => {
  try {
    // Use OpenAI to generate a personalized questionnaire based on the client data
    const prompt = `
      You are a business process expert creating a personalized audit questionnaire.
      Business Information:
      - Industry: ${clientData.companyProfile?.industry || 'Unknown'}
      - Business Size: ${clientData.companyProfile?.businessSize || 'Unknown'}
      - Customer Type: ${clientData.companyProfile?.customerType || 'Unknown'}
      - Positioning: ${clientData.companyProfile?.positioning || 'Unknown'}
      - Products/Services: ${clientData.companyProfile?.productsServices || 'Unknown'}
      - Business Challenge: ${clientData.challenge || 'Unknown'}
      - Target Result: ${clientData.target || 'Unknown'}
      Create a personalized business audit questionnaire with 5 questions for each of these areas:
      1. Sales
      2. Marketing
      3. Operations
      4. HR (Human Resources)
      5. Finance
      Make the questions specific to their industry, business size, and stated challenges when possible.
      Each question should help assess the maturity of their business processes.
      Format the response as a JSON object with this structure:
      {
        "sales": [
          { "id": "sales_1", "text": "Question text here" },
          ...
        ],
        "marketing": [...],
        "operations": [...],
        "hr": [...],
        "finance": [...]
      }
    `

    // Call OpenAI API through the proxy
    const response = await axios.post(
      'https://requests.magicpatterns.com/openai/proxy',
      {
        model: 'gpt-4-turbo',
        messages: [
          {
            role: 'system',
            content:
              'You are a business process expert that creates personalized audit questionnaires.',
          },
          {
            role: 'user',
            content: prompt,
          },
        ],
        temperature: 0.7,
      },
    )

    // Parse the response
    const aiResponse = response.data.choices[0].message.content

    // Try to parse JSON from the response
    try {
      const jsonMatch =
        aiResponse.match(/```json\n([\s\S]*?)\n```/) ||
        aiResponse.match(/```\n([\s\S]*?)\n```/) ||
        aiResponse.match(/{[\s\S]*?}/)
      const jsonContent = jsonMatch ? jsonMatch[1] || jsonMatch[0] : aiResponse
      const cleanedJson = jsonContent.replace(/```json|```/g, '').trim()
      return JSON.parse(cleanedJson)
    } catch (error) {
      console.error('Failed to parse questionnaire response:', error)
      // Fall back to mock data
      return fallbackQuestionnaire()
    }
  } catch (error) {
    console.error('Error generating questionnaire:', error)
    // Fall back to mock data
    return fallbackQuestionnaire()
  }
}

// Fallback questionnaire in case of API failure
const fallbackQuestionnaire = () => {
  return {
    sales: [
      {
        id: 'sales_1',
        text: 'Do you have a documented process for qualifying and tracking new client leads?',
      },
      {
        id: 'sales_2',
        text: 'Do you have a standardized proposal template that clearly communicates your value proposition?',
      },
      {
        id: 'sales_3',
        text: 'Do you track key sales metrics like conversion rate, average deal size, and sales cycle length?',
      },
      {
        id: 'sales_4',
        text: 'Do you have a client renewal or upsell process for existing clients?',
      },
      {
        id: 'sales_5',
        text: 'Do you regularly collect and analyze feedback from lost opportunities?',
      },
    ],
    marketing: [
      {
        id: 'marketing_1',
        text: 'Do you have a documented marketing strategy with clear goals and KPIs?',
      },
      {
        id: 'marketing_2',
        text: 'Do you maintain a consistent content calendar for your own marketing efforts?',
      },
      {
        id: 'marketing_3',
        text: 'Do you track the ROI of your marketing campaigns?',
      },
      {
        id: 'marketing_4',
        text: 'Do you have a system to capture and nurture marketing leads?',
      },
      {
        id: 'marketing_5',
        text: 'Do you regularly analyze your website traffic and conversion metrics?',
      },
    ],
    operations: [
      {
        id: 'operations_1',
        text: 'Do you have documented SOPs for your core service delivery processes?',
      },
      {
        id: 'operations_2',
        text: 'Do you use project management software to track client work?',
      },
      {
        id: 'operations_3',
        text: 'Do you have a quality control process before delivering work to clients?',
      },
      {
        id: 'operations_4',
        text: 'Do you regularly review and optimize your operational workflows?',
      },
      {
        id: 'operations_5',
        text: 'Do you have clear handoff procedures between teams or departments?',
      },
    ],
    hr: [
      {
        id: 'hr_1',
        text: 'Do you have standardized onboarding procedures for new team members?',
      },
      {
        id: 'hr_2',
        text: 'Do team members have clear job descriptions and KPIs?',
      },
      {
        id: 'hr_3',
        text: 'Do you conduct regular performance reviews with team members?',
      },
      {
        id: 'hr_4',
        text: 'Do you have a structured approach to professional development?',
      },
      {
        id: 'hr_5',
        text: 'Do you track team satisfaction and engagement metrics?',
      },
    ],
    finance: [
      {
        id: 'finance_1',
        text: 'Do you review financial statements at least monthly?',
      },
      {
        id: 'finance_2',
        text: 'Do you track project or service profitability?',
      },
      {
        id: 'finance_3',
        text: 'Do you have a cash flow forecasting process?',
      },
      {
        id: 'finance_4',
        text: 'Do you have a structured invoicing and collections process?',
      },
      {
        id: 'finance_5',
        text: 'Do you have a budget and regularly track actual vs. budgeted expenses?',
      },
    ],
  }
}

export const generateResults = async (
  clientData: any,
  auditResponses: any,
): Promise<any> => {
  try {
    // Use OpenAI to generate personalized results and recommendations
    const prompt = `
      You are a business process expert analyzing audit results.
      Business Information:
      - Industry: ${clientData.companyProfile?.industry || 'Unknown'}
      - Business Size: ${clientData.companyProfile?.businessSize || 'Unknown'}
      - Customer Type: ${clientData.companyProfile?.customerType || 'Unknown'}
      - Positioning: ${clientData.companyProfile?.positioning || 'Unknown'}
      - Products/Services: ${clientData.companyProfile?.productsServices || 'Unknown'}
      - Business Challenge: ${clientData.challenge || 'Unknown'}
      - Target Result: ${clientData.target || 'Unknown'}
      Audit Responses (1=Never, 5=Always):
      Sales: ${JSON.stringify(auditResponses.sales)}
      Marketing: ${JSON.stringify(auditResponses.marketing)}
      Operations: ${JSON.stringify(auditResponses.operations)}
      HR: ${JSON.stringify(auditResponses.hr)}
      Finance: ${JSON.stringify(auditResponses.finance)}
      Calculate scores for each area as a percentage (total points / maximum possible points * 100).
      Based on the scores and business information, provide:
      1. An overall assessment of the business
      2. Key strengths (4-5 items)
      3. Key weaknesses (4-5 items)
      4. Business impact of these issues
      5. Five specific, actionable recommendations that are:
         - Low-cost to implement
         - High-impact
         - Relevant to their industry and challenges
         - Detailed with specific steps
      Format your response as a JSON object with this structure:
      {
        "functionalScores": {
          "sales": number,
          "marketing": number,
          "operations": number,
          "hr": number,
          "finance": number
        },
        "overallScore": number,
        "summary": {
          "overall": "string",
          "strengths": ["string", "string", ...],
          "weaknesses": ["string", "string", ...],
          "businessImpact": "string"
        },
        "recommendations": [
          {
            "title": "string",
            "description": "string",
            "impact": "string",
            "difficulty": "Easy|Medium|Hard",
            "function": "string"
          },
          ...
        ]
      }
    `

    // Call OpenAI API through the proxy
    const response = await axios.post(
      'https://requests.magicpatterns.com/openai/proxy',
      {
        model: 'gpt-4-turbo',
        messages: [
          {
            role: 'system',
            content:
              'You are a business process expert analyzing audit results.',
          },
          {
            role: 'user',
            content: prompt,
          },
        ],
        temperature: 0.7,
      },
    )

    // Parse the response
    const aiResponse = response.data.choices[0].message.content

    // Try to parse JSON from the response
    try {
      const jsonMatch =
        aiResponse.match(/```json\n([\s\S]*?)\n```/) ||
        aiResponse.match(/```\n([\s\S]*?)\n```/) ||
        aiResponse.match(/{[\s\S]*?}/)
      const jsonContent = jsonMatch ? jsonMatch[1] || jsonMatch[0] : aiResponse
      const cleanedJson = jsonContent.replace(/```json|```/g, '').trim()

      // Parse and validate the results
      const results = JSON.parse(cleanedJson)

      // Calculate scores if not provided or invalid
      if (!results.functionalScores) {
        results.functionalScores = calculateScores(auditResponses)
      }
      if (!results.overallScore) {
        results.overallScore = calculateOverallScore(results.functionalScores)
      }

      return results
    } catch (error) {
      console.error('Failed to parse results response:', error)
      // Fall back to calculated results
      return fallbackResults(auditResponses)
    }
  } catch (error) {
    console.error('Error generating results:', error)
    // Fall back to calculated results
    return fallbackResults(auditResponses)
  }
}

// Helper function to calculate scores
const calculateScores = (auditResponses: any) => {
  const calculateScore = (responses: Record<string, number>) => {
    if (Object.keys(responses).length === 0) return 0
    const total = Object.values(responses).reduce((sum, val) => sum + val, 0)
    const max = Object.keys(responses).length * 5
    return Math.round((total / max) * 100)
  }
  return {
    sales: calculateScore(auditResponses.sales),
    marketing: calculateScore(auditResponses.marketing),
    operations: calculateScore(auditResponses.operations),
    hr: calculateScore(auditResponses.hr),
    finance: calculateScore(auditResponses.finance),
  }
}

// Helper function to calculate overall score
const calculateOverallScore = (functionalScores: any) => {
  return Math.round(
    Object.values(functionalScores).reduce(
      (sum: number, score: any) => sum + score,
      0,
    ) / Object.keys(functionalScores).length,
  )
}

// Fallback results in case of API failure
const fallbackResults = (auditResponses: any) => {
  const functionalScores = calculateScores(auditResponses)
  const overallScore = calculateOverallScore(functionalScores)

  return {
    overallScore,
    functionalScores,
    summary: {
      overall:
        'Based on your responses, there are several areas where implementing structured business processes could significantly improve performance and scalability.',
      weaknesses: [
        'Lack of standardized processes in key business areas',
        'Inconsistent tracking of important metrics',
        'Limited documentation of operational procedures',
        'Reactive rather than proactive approach to business challenges',
      ],
      strengths: [
        'Core business expertise and technical capabilities',
        'Awareness of the need for improved processes',
        'Commitment to business improvement',
        'Existing customer relationships and market presence',
      ],
      businessImpact:
        'The identified process gaps are likely reducing operational efficiency by 20-30% and limiting your ability to scale. Implementing structured processes in your lowest-scoring areas could improve profitability and reduce daily stress within 60-90 days.',
    },
    recommendations: [
      {
        title: 'Implement a Basic CRM System',
        description:
          'Set up a simple CRM tool (even a spreadsheet) to track all customer interactions, deals, and follow-ups in one place.',
        impact:
          'Increase lead conversion by 15-25% by ensuring no opportunities fall through the cracks.',
        difficulty: 'Easy',
        function: 'Sales',
      },
      {
        title: 'Create a Weekly Cash Flow Tracker',
        description:
          'Develop a simple spreadsheet to monitor incoming and outgoing cash on a weekly basis.',
        impact:
          'Improve financial visibility and avoid cash crunches by identifying issues 3-4 weeks in advance.',
        difficulty: 'Easy',
        function: 'Finance',
      },
      {
        title: 'Document Your Core Service Delivery Process',
        description:
          'Create a visual workflow for your main service/product delivery process with clear steps and responsibilities.',
        impact:
          'Reduce delivery time by 20% and improve consistency by ensuring everyone follows the same optimized process.',
        difficulty: 'Medium',
        function: 'Operations',
      },
      {
        title: 'Implement a Basic Marketing Calendar',
        description:
          'Create a 90-day content and promotion schedule to plan and track your marketing activities.',
        impact:
          'Increase brand visibility and lead generation through consistent, planned marketing efforts.',
        difficulty: 'Medium',
        function: 'Marketing',
      },
      {
        title: 'Create Simple Role Descriptions',
        description:
          'Develop one-page descriptions for each team role with key responsibilities and success metrics.',
        impact:
          'Improve team productivity by 15-20% by clarifying expectations and reducing confusion.',
        difficulty: 'Medium',
        function: 'HR',
      },
    ],
  }
}
